import React, { useEffect } from "react";
import JsonLd from "./JsonLd";

export interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

interface SEOProps extends SEOConfig {}

/**
 * Per-route head injector.
 * - Sets <title>, <meta name="description">, OG/Twitter cards, canonical, robots
 * - Renders any JSON-LD schemas inline
 *
 * Note: this runs client-side under CRA. Once `react-snap` is wired (D-004) the
 * head tags are baked into the prerendered HTML at build time. Until migration,
 * crawlers that execute JS will still pick these up.
 */
export function SEO({
  title,
  description,
  canonical,
  ogImage = "https://whobrew.us/og-default.png",
  noindex = false,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    setMeta("name", "twitter:card", "summary_large_image");

    if (canonical) setLink("canonical", canonical);
    setMeta(
      "name",
      "robots",
      noindex ? "noindex,nofollow" : "index,follow,max-snippet:-1,max-image-preview:large"
    );
  }, [title, description, canonical, ogImage, noindex]);

  return jsonLd ? <JsonLd schema={jsonLd} /> : null;
}

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default SEO;
